import React, { useEffect } from "react";
import { motion } from "framer-motion";

const sizes = [
  { size: "S", chest: "38", length: "27", shoulder: "16", sleeve: "8" },
  { size: "M", chest: "40", length: "28", shoulder: "17", sleeve: "8.5" },
  { size: "L", chest: "42", length: "29", shoulder: "18", sleeve: "9" },
  { size: "XL", chest: "44", length: "30", shoulder: "19", sleeve: "9.5" },
  { size: "XXL", chest: "46", length: "31", shoulder: "20", sleeve: "10" },
];

export default function SizeGuide() {
  useEffect(() => {
    document.title = "Size Guide | Cynos";
  }, []);

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-6xl space-y-12">
        <div className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Size Guide</p>
          <h1 className="mt-4 text-5xl font-anton uppercase text-white">Oversized tee sizing</h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-400">Use the chart below to choose the ideal fit. For oversized streetwear, your normal size is usually the best starting point.</p>
        </div>

        <section className="overflow-x-auto rounded-3xl border border-white/10 bg-black/40 p-6 shadow-xl shadow-black/20">
          <table className="min-w-full border-collapse text-left text-sm text-zinc-300">
            <thead>
              <tr className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                <th className="border-b border-white/10 px-4 py-3">Size</th>
                <th className="border-b border-white/10 px-4 py-3">Chest (in)</th>
                <th className="border-b border-white/10 px-4 py-3">Length (in)</th>
                <th className="border-b border-white/10 px-4 py-3">Shoulder (in)</th>
                <th className="border-b border-white/10 px-4 py-3">Sleeve (in)</th>
              </tr>
            </thead>
            <tbody>
              {sizes.map((row) => (
                <tr key={row.size} className="border-b border-white/10 transition hover:bg-white/5">
                  <td className="px-4 py-4 font-semibold text-white">{row.size}</td>
                  <td className="px-4 py-4">{row.chest}</td>
                  <td className="px-4 py-4">{row.length}</td>
                  <td className="px-4 py-4">{row.shoulder}</td>
                  <td className="px-4 py-4">{row.sleeve}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="grid gap-8 xl:grid-cols-2">
          <div className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
            <h2 className="text-3xl font-semibold text-white">How to Measure</h2>
            <div className="mt-6 space-y-4 text-sm text-zinc-300">
              <div>
                <p className="font-semibold text-white">Chest</p>
                <p>Measure around the fullest part of your chest, keeping the tape horizontal.</p>
              </div>
              <div>
                <p className="font-semibold text-white">Length</p>
                <p>Measure from the highest point of the shoulder to the hem.</p>
              </div>
              <div>
                <p className="font-semibold text-white">Shoulder</p>
                <p>Measure from the edge of one shoulder seam to the other.</p>
              </div>
              <div>
                <p className="font-semibold text-white">Sleeve</p>
                <p>Measure from the shoulder seam to the end of the sleeve.</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-black/40 p-8 shadow-xl shadow-black/20">
            <h2 className="text-3xl font-semibold text-white">Fit Guide</h2>
            <div className="mt-6 space-y-4 text-sm text-zinc-300">
              <div>
                <p className="font-semibold text-white">Regular fit</p>
                <p>Choose your usual size for a balanced streetwear look with structure.</p>
              </div>
              <div>
                <p className="font-semibold text-white">Oversized fit</p>
                <p>Choose your normal size for the relaxed, boxy Cynos aesthetic.</p>
              </div>
              <div>
                <p className="font-semibold text-white">Relaxed streetwear fit</p>
                <p>Size up for a more dramatic, oversized silhouette with extra drop shoulder.</p>
              </div>
            </div>
          </div>
        </section>

        <div className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <p className="text-sm text-zinc-400">Measurements may vary by ±0.5 inch. For oversized fit, choose your regular size. For a more relaxed look, choose one size up.</p>
        </div>
      </motion.div>
    </main>
  );
}
